/// Polls GET /v1/config. Tracks API reachability separately from device online.

import { create } from 'zustand';

import { appEnv } from '@/app/env/appEnv';
import { fetchRemoteConfig } from '../data/remoteConfigRepository';
import { compareSemver, minimumVersionForWeb, type RemoteAppConfig } from '../domain/remoteAppConfig';

export type AppGate = 'normal' | 'maintenance' | 'forceUpdate';

const prodPollIntervalMs = 10 * 60 * 1000;
const remoteDevPollIntervalMs = 15 * 1000;
const localPollIntervalMs = 5 * 1000;
/** Fast poll while maintenance / force-update / server-unavailable is open. */
export const gateScreenPollIntervalMs = 2 * 1000;

function pollIntervalMs(): number {
  if (appEnv.isLocal) return localPollIntervalMs;
  if (appEnv.isDev) return remoteDevPollIntervalMs;
  return prodPollIntervalMs;
}

/** Call from gate screens; returns cleanup. Dev/local by default; force for all envs. */
export function startGateScreenPolling(options?: { force?: boolean }): () => void {
  if (!options?.force && !appEnv.isDev) return () => undefined;
  void useRemoteConfigStore.getState().refresh();
  const id = window.setInterval(() => {
    void useRemoteConfigStore.getState().refresh();
  }, gateScreenPollIntervalMs);
  return () => window.clearInterval(id);
}

interface RemoteConfigState {
  config: RemoteAppConfig | null;
  /** True after the first fetch attempt (success or fail). */
  ready: boolean;
  /** Whether the last API poll succeeded. Optimistic until the first attempt. */
  serverReachable: boolean;
  refresh: () => Promise<void>;
}

export const useRemoteConfigStore = create<RemoteConfigState>((set) => ({
  config: null,
  ready: false,
  serverReachable: true,
  refresh: async () => {
    try {
      const config = await fetchRemoteConfig();
      set({ config, ready: true, serverReachable: true });
    } catch (error) {
      console.warn('RemoteConfigStore.refresh failed', error);
      set({ ready: true, serverReachable: false });
    }
  },
}));

let started = false;

/** Idempotent: App mounts this once. */
export function startRemoteConfigPolling(): void {
  if (started) return;
  started = true;

  void useRemoteConfigStore.getState().refresh();
  window.setInterval(() => {
    void useRemoteConfigStore.getState().refresh();
  }, pollIntervalMs());

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) void useRemoteConfigStore.getState().refresh();
  });
}

export function selectAppGate(config: RemoteAppConfig | null): AppGate {
  if (!config) return 'normal';
  if (config.isMaintenance) return 'maintenance';
  if (compareSemver(appEnv.version, minimumVersionForWeb(config)) < 0) return 'forceUpdate';
  return 'normal';
}

export function useAppGate(): AppGate {
  return useRemoteConfigStore((state) => selectAppGate(state.config));
}

export function useServerReachable(): boolean {
  return useRemoteConfigStore((state) => state.serverReachable);
}
